import React, { useCallback, useMemo, useState } from 'react'
import classes from './TableArea.module.scss'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table'
import {
  TiArrowSortedUp,
  TiArrowSortedDown,
  TiArrowUnsorted,
} from 'react-icons/ti'
import EditIcon from '@mui/icons-material/Edit'
import Pagination from './Pagination'
import { useMediaQuery } from '@material-ui/core'
import EditUserForm from '../modal/EditUserForm'
import DeleteIcon from '@material-ui/icons/Delete'
import { ConfirmModal } from '../../../UIkit/modal/ConfirmModal'
import { useDispatch } from 'react-redux'
import { deleteUser } from '../../../../reducks/users/operations'

const TableArea = ({ users, isAdmin }) => {
  const dispatch = useDispatch()

  const [sorting, setSorting] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [keyword, setKeyword] = useState('')
  const [columnFilters, setColumnFilters] = useState([])
  const [pageSize, setPageSize] = useState(15)
  const [pageIndex, setPageIndex] = useState(0)
  const isIphone = useMediaQuery('(max-width:600px)')
  const isMobile = useMediaQuery('(max-width:960px)')
  const [isShowDialog, setIsShowDialog] = useState(false)
  const [isShowDeleteDialog, setIsShowDeleteDialog] = useState(false)
  const [editUser, setEditUser] = useState(null)
  const [deleteId, setDeleteId] = useState(null)

  const handleEditClick = useCallback(
    (user) => {
      setIsShowDialog(true)
      setEditUser(user)
    },
    [setIsShowDialog, setEditUser]
  )

  const handleDeleteClick = useCallback((id) => {
    setIsShowDeleteDialog(true)
    setDeleteId(id)
  }, [])

  const handleDeleteUser = useCallback(() => {
    dispatch(deleteUser(deleteId))
    setDeleteId(null)
    setIsShowDeleteDialog(false)
  }, [deleteId, dispatch])

  // フィルタ適用のためのヘルパー関数
  const applyFilter = useCallback(() => {
    setGlobalFilter(keyword)
  }, [keyword])

  // エンターキーの処理
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        applyFilter()
      }
    },
    [applyFilter]
  )

  const columns = useMemo(() => {
    return [
      {
        id: 'edit',
        header: '',
        cell: ({ row }) => (
          <button
            className={classes.icon}
            onClick={() => handleEditClick(row.original)}
          >
            <EditIcon />
          </button>
        ),
        enableSorting: false,
        size: 50,
      },
      {
        id: 'usernumber',
        accessorKey: 'usernumber',
        header: '番号',
        cell: ({ getValue }) => (
          <div className={classes.workerId}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'username',
        accessorKey: 'username',
        header: '社員名',
        cell: ({ getValue }) => (
          <div className={classes.workerName}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'departmentid',
        accessorKey: 'departmentid',
        header: '所属',
        cell: ({ getValue }) => (
          <div className={classes.departmentId}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'departmentname',
        accessorKey: 'departmentname',
        header: '所属名',
        cell: ({ getValue }) => (
          <div className={classes.departmentName}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'メール',
        cell: ({ getValue }) => (
          <div className={classes.email}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'order_email',
        accessorKey: 'order_email',
        header: '発注担当者メール',
        cell: ({ getValue }) => (
          <div className={classes.email}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'role',
        accessorKey: 'role',
        header: '権限',
        cell: ({ getValue }) => (
          <div className={classes.role}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'orderDepartment',
        header: '発注部署',
        accessorFn: (row) =>
          `${row.order_base_name} ${row.order_department_name}`,
        cell: ({ getValue }) => (
          <div className={classes.order}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'isSend',
        accessorKey: 'isSend',
        header: '送信',
        cell: ({ getValue }) => (
          <div className={classes.isSend}>{getValue() ? 'する' : 'しない'}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'sendTime',
        accessorKey: 'sendTime',
        header: '時刻',
        cell: ({ getValue }) => (
          <div className={classes.sendTime}>{getValue()}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days0',
        accessorKey: 'days',
        header: '日',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[0].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days1',
        accessorKey: 'days',
        header: '月',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[1].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days2',
        accessorKey: 'days',
        header: '火',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[2].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days3',
        accessorKey: 'days',
        header: '水',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[3].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days4',
        accessorKey: 'days',
        header: '木',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[4].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days5',
        accessorKey: 'days',
        header: '金',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[5].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'days6',
        accessorKey: 'days',
        header: '土',
        cell: ({ getValue }) => (
          <div className={classes.days}>{getValue()[6].isSend ? '〇' : ''}</div>
        ),
        enableColumnFilter: true,
      },
      {
        id: 'delete',
        header: '',
        cell: ({ row }) => (
          <button
            disabled={!isAdmin}
            className={classes.icon}
            onClick={() => handleDeleteClick(row.original.id)}
          >
            <DeleteIcon />
          </button>
        ),
        enableSorting: false,
        size: 50,
      },
    ]
  }, [isMobile, isIphone, handleEditClick, handleDeleteClick])

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination: {
        pageSize,
        pageIndex,
      },
      columnFilters,
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function'
          ? updater({ pageSize, pageIndex })
          : updater
      setPageSize(newPagination.pageSize)
      setPageIndex(newPagination.pageIndex)
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  // 表示範囲の計算
  const filteredRowCount = table.getFilteredRowModel().rows.length
  const currentPageRowCount = table.getRowModel().rows.length
  const startIndex = pageIndex * pageSize + 1
  const endIndex = Math.min(
    startIndex + currentPageRowCount - 1,
    filteredRowCount
  )

  return (
    <div className={classes.root}>
      {/* フィルタ入力欄と件数表示 */}
      <div className={classes.menu}>
        <div className={classes.filter}>
          <input
            value={keyword ?? ''}
            onChange={(e) => setKeyword(e.target.value)}
            onBlur={applyFilter}
            onKeyDown={handleKeyDown}
            placeholder="検索..."
          />
        </div>

        {/* 件数表示エリア */}
        <div className={classes.countDisplay}>
          <span className={classes.countText}>
            総件数: {filteredRowCount}件
            {filteredRowCount > 0 && (
              <>
                {' '}
                | 表示: {startIndex}〜{endIndex}件
              </>
            )}
          </span>
        </div>

        {/* Paginationコンポーネント */}
        <Pagination
          table={table}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </div>

      <div className={classes.container}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{
                      cursor: header.column.getCanSort()
                        ? 'pointer'
                        : 'default',
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                      position: 'relative',
                    }}
                  >
                    <div className={classes.headerCell}>
                      <div className={classes.headerContent}>
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                        {header.column.getCanSort() &&
                          (header.column.getIsSorted() === 'asc' ? (
                            <TiArrowSortedUp />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <TiArrowSortedDown />
                          ) : (
                            <TiArrowUnsorted />
                          ))}
                      </div>
                      {header.column.getCanFilter() && (
                        <div className={classes.headerFilter}>
                          <input
                            value={header.column.getFilterValue() || ''}
                            onChange={(e) =>
                              header.column.setFilterValue(e.target.value)
                            }
                            placeholder="フィルタ..."
                            className={classes.filterInput}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={
                      (cell.column.id.includes('days') ||
                        cell.column.id === 'sendTime' ||
                        cell.column.id === 'isSend') &&
                      !row.original.isSend
                        ? classes.isSendFalse
                        : ''
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isShowDialog && (
        <EditUserForm setIsShow={setIsShowDialog} prevInfo={editUser} />
      )}
      {isShowDeleteDialog && deleteId && (
        <ConfirmModal
          setIsShow={setIsShowDeleteDialog}
          title="確認"
          message={'元に戻せません。よろしいですか？'}
          onExec={handleDeleteUser}
          onCancel={() => setIsShowDeleteDialog(false)}
        />
      )}
    </div>
  )
}

export default TableArea
